import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const numberFormat = new Intl.NumberFormat();

function MetricCard({ title, value, subtitle, icon, onClick }) {
  return (
    <div className="col-md-6 col-xl-3">
      <div
        className="card border-0 shadow-sm h-100"
        onClick={onClick}
        role={onClick ? "button" : undefined}
        style={onClick ? { cursor: "pointer" } : undefined}
      >
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div
                className="text-uppercase text-muted fw-semibold"
                style={{ fontSize: "0.72rem", letterSpacing: "0.05em" }}
              >
                {title}
              </div>
              <div className="display-6 fw-bold mt-2">{value}</div>
              {subtitle && <div className="text-muted small mt-1">{subtitle}</div>}
            </div>
            <div
              className="rounded-3 d-flex align-items-center justify-content-center"
              style={{ width: 46, height: 46, background: "#eef7f0", color: "#125b2d" }}
            >
              <i className={`bi ${icon} fs-4`}></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InsightRow({ label, value, note }) {
  return (
    <div className="d-flex justify-content-between align-items-center border-bottom py-3 gap-3">
      <div>
        <div className="fw-semibold">{label}</div>
        {note && <small className="text-muted">{note}</small>}
      </div>
      <div className="fw-bold fs-5 text-nowrap">{value}</div>
    </div>
  );
}

function DeputyHeadDashboard() {
  const [summary, setSummary] = useState(null);
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailModal, setDetailModal] = useState(null);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [summaryResponse, communicationsResponse] = await Promise.all([
        axiosInstance.get("headmaster/summary/"),
        axiosInstance.get("headmaster/announcements/"),
      ]);
      setSummary(summaryResponse.data || {});
      setCommunications(communicationsResponse.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load Deputy Head's Office dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const people = summary?.people || {};
  const academic = summary?.academic || {};
  const approvals = summary?.approvals || {};
  const classGroups = summary?.class_groups || [];
  const teacherWorkload = summary?.teacher_workload || [];
  const upcomingEvents = summary?.upcoming_events || [];
  const upcomingTrips = summary?.upcoming_trips || [];

  const analytics = useMemo(() => {
    const activeClasses = classGroups.filter((item) => item.is_active).length;
    const classesWithTeacher = classGroups.filter((item) => item.class_teacher).length;
    const classesWithoutTeacher = Math.max(classGroups.length - classesWithTeacher, 0);
    const teacherCoverage = classGroups.length > 0
      ? Math.round((classesWithTeacher / classGroups.length) * 100)
      : 0;
    const totalAllocations = teacherWorkload.reduce(
      (sum, item) => sum + Number(item.allocations_count || 0),
      0
    );
    const averageWorkload = teacherWorkload.length > 0
      ? (totalAllocations / teacherWorkload.length).toFixed(1)
      : "0.0";
    const teachersWithoutAllocations = teacherWorkload.filter(
      (item) => Number(item.allocations_count || 0) === 0
    ).length;

    return {
      activeClasses,
      classesWithTeacher,
      classesWithoutTeacher,
      teacherCoverage,
      totalAllocations,
      averageWorkload,
      teachersWithoutAllocations,
    };
  }, [classGroups, teacherWorkload]);

  const recentCommunications = communications
    .filter((item) => item.source === "Deputy Head's Office")
    .slice(0, 5);

  const openDetail = (type) => setDetailModal(type);
  const closeDetail = () => setDetailModal(null);

  if (loading && !summary) {
    return (
      <div className="container-fluid py-4">
        <div className="card border-0 shadow-sm">
          <div className="card-body p-5 text-center">
            <div className="spinner-border text-success mb-3"></div>
            <div className="fw-semibold">Loading Deputy Head's Office dashboard...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <div
            className="text-uppercase fw-semibold text-muted mb-1"
            style={{ letterSpacing: "0.08em", fontSize: "0.72rem" }}
          >
            Executive Overview
          </div>
          <h3 className="fw-bold mb-1" style={{ color: "#125b2d" }}>
            Deputy Head's Office Dashboard
          </h3>
          <p className="text-muted mb-0">School-wide leadership insights and key indicators.</p>
        </div>
        <button className="btn btn-outline-success" onClick={loadDashboard} disabled={loading}>
          <i className="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </button>
      </div>

      <div className="row g-3 mb-4">
        <MetricCard
          title="Students"
          value={numberFormat.format(people.students || 0)}
          subtitle="Registered students"
          icon="bi-mortarboard"
          onClick={() => openDetail("students")}
        />
        <MetricCard
          title="Teachers"
          value={numberFormat.format(people.teachers || 0)}
          subtitle="Teaching staff"
          icon="bi-person-workspace"
          onClick={() => openDetail("teachers")}
        />
        <MetricCard
          title="Active Classes"
          value={numberFormat.format(analytics.activeClasses)}
          subtitle={`${academic.active_year || "Current academic year"}`}
          icon="bi-building"
          onClick={() => openDetail("classes")}
        />
        <MetricCard
          title="Class Teacher Coverage"
          value={`${analytics.teacherCoverage}%`}
          subtitle={`${analytics.classesWithTeacher} of ${classGroups.length} classes assigned`}
          icon="bi-person-check"
          onClick={() => openDetail("coverage")}
        />
      </div>

      <div className="row g-4 mb-4">
        <div className="col-xl-7">
          <div
            className="card border-0 shadow-sm h-100"
            role="button"
            style={{ cursor: "pointer" }}
            onClick={() => openDetail("academic")}
          >
            <div className="card-header bg-white border-0 pt-4 px-4">
              <h5 className="fw-bold mb-1">Academic & Staffing Overview</h5>
              <small className="text-muted">High-level indicators for leadership review.</small>
            </div>
            <div className="card-body px-4">
              <InsightRow label="Academic Year" value={academic.active_year || "Not set"} />
              <InsightRow label="Current Term" value={academic.active_term || "Not set"} />
              <InsightRow
                label="Class Groups"
                value={numberFormat.format(academic.class_groups || classGroups.length || 0)}
              />
              <InsightRow
                label="Teaching Allocations"
                value={numberFormat.format(academic.teacher_allocations || 0)}
                note={`Average ${analytics.averageWorkload} allocations per teacher`}
              />
              <InsightRow
                label="Classes Without Class Teacher"
                value={analytics.classesWithoutTeacher}
                note={
                  analytics.classesWithoutTeacher > 0
                    ? "Leadership attention may be required"
                    : "All listed classes have class teachers"
                }
              />
              <InsightRow
                label="Teachers Without Allocations"
                value={analytics.teachersWithoutAllocations}
              />
            </div>
          </div>
        </div>

        <div className="col-xl-5">
          <div
            className="card border-0 shadow-sm h-100"
            role="button"
            style={{ cursor: "pointer" }}
            onClick={() => openDetail("attention")}
          >
            <div className="card-header bg-white border-0 pt-4 px-4">
              <h5 className="fw-bold mb-1">Leadership Attention</h5>
              <small className="text-muted">Matters that may require review.</small>
            </div>
            <div className="card-body px-4">
              <InsightRow label="Pending Teacher Accounts" value={approvals.pending_teachers || 0} />
              <InsightRow label="Pending Parent Accounts" value={approvals.pending_parents || 0} />
              <InsightRow label="Pending Parent Claims" value={approvals.pending_parent_claims || 0} />
              <InsightRow label="Upcoming School Events" value={upcomingEvents.length} />
              <InsightRow label="Upcoming Trips" value={upcomingTrips.length} />
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-xl-7">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-0 pt-4 px-4">
              <h5 className="fw-bold mb-1">Teacher Workload Snapshot</h5>
              <small className="text-muted">Teaching allocation overview. This is read-only.</small>
            </div>
            <div className="card-body px-4">
              {teacherWorkload.length === 0 ? (
                <div className="text-muted py-3">No teacher workload information available.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Teacher</th>
                        <th>Staff No.</th>
                        <th className="text-end">Allocations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teacherWorkload
                        .slice()
                        .sort((a, b) => Number(b.allocations_count || 0) - Number(a.allocations_count || 0))
                        .slice(0, 10)
                        .map((teacher) => (
                          <tr key={teacher.id}>
                            <td className="fw-semibold">{teacher.full_name}</td>
                            <td>{teacher.staff_number || "—"}</td>
                            <td className="text-end fw-bold">{teacher.allocations_count || 0}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-xl-5">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-0 pt-4 px-4">
              <h5 className="fw-bold mb-1">Upcoming School Activity</h5>
              <small className="text-muted">Events and educational visits.</small>
            </div>
            <div className="card-body px-4">
              {upcomingEvents.length === 0 ? (
                <div className="text-muted py-3">No upcoming events currently scheduled.</div>
              ) : (
                upcomingEvents.slice(0, 6).map((event) => (
                  <div key={event.id} className="border-bottom py-3">
                    <div className="d-flex justify-content-between gap-3">
                      <div>
                        <div className="fw-semibold">{event.title}</div>
                        <small className="text-muted">
                          {event.event_type === "TRIP"
                            ? "Educational Visit / Trip"
                            : (event.event_type || "School Event").replaceAll("_", " ")}
                        </small>
                      </div>
                      <div className="text-end small">
                        <div className="fw-semibold">{event.start_date}</div>
                        <div className="text-muted">{event.start_time || ""}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white border-0 pt-4 px-4">
          <h5 className="fw-bold mb-1">Recent Deputy Head Communications</h5>
          <small className="text-muted">Latest communications created by Deputy Head's Office.</small>
        </div>
        <div className="card-body px-4">
          {recentCommunications.length === 0 ? (
            <div className="text-muted py-3">No Deputy Head communications yet.</div>
          ) : (
            recentCommunications.map((item) => (
              <div key={item.id} className="border-bottom py-3">
                <div className="d-flex justify-content-between gap-3">
                  <div>
                    <div className="fw-semibold">{item.title}</div>
                    <small className="text-muted">Audience: {item.audience || "ALL"}</small>
                  </div>
                  <div>
                    <span className="badge bg-light text-dark border">
                      {item.delivery_status || "DRAFT"}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {detailModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,.45)" }}
          onClick={closeDetail}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-scrollable"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-content border-0 shadow">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title fw-bold">
                    {detailModal === "academic" && "Academic & Staffing Details"}
                    {detailModal === "attention" && "Leadership Attention Details"}
                    {detailModal === "students" && "Student Population Snapshot"}
                    {detailModal === "teachers" && "Teacher Staffing Snapshot"}
                    {detailModal === "classes" && "Active Class Overview"}
                    {detailModal === "coverage" && "Class Teacher Coverage"}
                  </h5>
                  <small className="text-muted">Read-only leadership information</small>
                </div>
                <button type="button" className="btn-close" onClick={closeDetail}></button>
              </div>

              <div className="modal-body">
                {detailModal === "academic" && (
                  <>
                    <div className="row g-3 mb-4">
                      <div className="col-md-3">
                        <div className="border rounded p-3">
                          <small className="text-muted">Academic Year</small>
                          <div className="fw-bold fs-5">{academic.active_year || "Not set"}</div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="border rounded p-3">
                          <small className="text-muted">Current Term</small>
                          <div className="fw-bold fs-5">{academic.active_term || "Not set"}</div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="border rounded p-3">
                          <small className="text-muted">Class Groups</small>
                          <div className="fw-bold fs-5">{academic.class_groups || classGroups.length || 0}</div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="border rounded p-3">
                          <small className="text-muted">Teaching Allocations</small>
                          <div className="fw-bold fs-5">{academic.teacher_allocations || 0}</div>
                        </div>
                      </div>
                    </div>

                    <h6 className="fw-bold">Class Groups</h6>
                    <div className="table-responsive mb-4">
                      <table className="table align-middle">
                        <thead>
                          <tr>
                            <th>Class</th>
                            <th>Class Teacher</th>
                            <th>Subjects</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {classGroups.map((group) => (
                            <tr key={group.id}>
                              <td className="fw-semibold">{group.name}</td>
                              <td>{group.class_teacher || "Not Assigned"}</td>
                              <td>{group.subjects_count || 0}</td>
                              <td>{group.is_active ? "Active" : "Inactive"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <h6 className="fw-bold">Teacher Allocation Snapshot</h6>
                    <div className="table-responsive">
                      <table className="table align-middle">
                        <thead>
                          <tr>
                            <th>Teacher</th>
                            <th>Staff No.</th>
                            <th className="text-end">Allocations</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teacherWorkload.map((teacher) => (
                            <tr key={teacher.id}>
                              <td className="fw-semibold">{teacher.full_name}</td>
                              <td>{teacher.staff_number || "—"}</td>
                              <td className="text-end">{teacher.allocations_count || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {detailModal === "attention" && (
                  <>
                    <InsightRow label="Pending Teacher Accounts" value={approvals.pending_teachers || 0} />
                    <InsightRow label="Pending Parent Accounts" value={approvals.pending_parents || 0} />
                    <InsightRow label="Pending Parent Claims" value={approvals.pending_parent_claims || 0} />
                    <InsightRow label="Upcoming School Events" value={upcomingEvents.length} />
                    <InsightRow label="Upcoming Trips" value={upcomingTrips.length} />

                    <h6 className="fw-bold mt-4">Upcoming Activity</h6>
                    {upcomingEvents.length === 0 ? (
                      <div className="text-muted">No upcoming events.</div>
                    ) : (
                      upcomingEvents.map((event) => (
                        <div className="border-bottom py-3" key={event.id}>
                          <div className="fw-semibold">{event.title}</div>
                          <small className="text-muted">
                            {event.start_date}{event.location ? ` · ${event.location}` : ""}
                          </small>
                        </div>
                      ))
                    )}
                  </>
                )}

                {detailModal === "students" && (
                  <>
                    <InsightRow label="Registered Students" value={people.students || 0} />
                    <InsightRow label="Active Users" value={people.active_users || 0} />
                    <InsightRow label="Inactive Users" value={people.inactive_users || 0} />
                    <div className="alert alert-light border mt-4 mb-0">
                      Detailed student academic and attendance analytics will appear here once those datasets are added to the leadership summary API.
                    </div>
                  </>
                )}

                {detailModal === "teachers" && (
                  <>
                    <InsightRow label="Teaching Staff" value={people.teachers || 0} />
                    <InsightRow label="Teaching Allocations" value={analytics.totalAllocations} />
                    <InsightRow label="Average Allocations Per Teacher" value={analytics.averageWorkload} />
                    <InsightRow label="Teachers Without Allocations" value={analytics.teachersWithoutAllocations} />
                  </>
                )}

                {detailModal === "classes" && (
                  <div className="table-responsive">
                    <table className="table align-middle">
                      <thead>
                        <tr>
                          <th>Class</th>
                          <th>Class Teacher</th>
                          <th>Subjects</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {classGroups.map((group) => (
                          <tr key={group.id}>
                            <td className="fw-semibold">{group.name}</td>
                            <td>{group.class_teacher || "Not Assigned"}</td>
                            <td>{group.subjects_count || 0}</td>
                            <td>{group.is_active ? "Active" : "Inactive"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {detailModal === "coverage" && (
                  <>
                    <InsightRow label="Class Teacher Coverage" value={`${analytics.teacherCoverage}%`} />
                    <InsightRow label="Classes With Class Teacher" value={analytics.classesWithTeacher} />
                    <InsightRow label="Classes Without Class Teacher" value={analytics.classesWithoutTeacher} />

                    <h6 className="fw-bold mt-4">Classes Without Assigned Class Teacher</h6>
                    {classGroups.filter((group) => !group.class_teacher).length === 0 ? (
                      <div className="text-muted">All classes have assigned class teachers.</div>
                    ) : (
                      classGroups
                        .filter((group) => !group.class_teacher)
                        .map((group) => (
                          <div className="border-bottom py-3 fw-semibold" key={group.id}>
                            {group.name}
                          </div>
                        ))
                    )}
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeDetail}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeputyHeadDashboard;
